import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users,
  Search,
  Plus,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { customerService } from "@/lib/database";
import type { Customer } from "@/lib/supabase";
import { Link, useNavigate } from "react-router-dom";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const itemsPerPage = 20;
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, [currentPage]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      const { customers: data, total } = await customerService.getAll(
        itemsPerPage,
        offset
      );

      setCustomers(data);
      setTotalCustomers(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error("Error loading customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between space-x-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            All Customers
          </h1>
          <p className="text-gray-600">
            Manage customer accounts and information
          </p>
        </div>

        <Link to="/admin/customers/new">
          <Button className="bg-[#254164] hover:bg-[#1a2d47]">
            <Plus className="h-4 w-4 mr-2" />
            New Customer
          </Button>
        </Link>
      </div>

      {/* Customers List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl md:text-2xl">
                Customers ({totalCustomers})
              </CardTitle>
              <CardDescription>
                All registered customers throughout the platform
              </CardDescription>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search customers..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <Users className="h-5 w-5 text-gray-400" />

                  <div>
                    <p className="font-medium text-gray-900">
                      {customer.first_name} {customer.last_name}
                    </p>
                    <p className="text-sm text-gray-600">{customer.email}</p>
                  </div>
                </div>

                <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                  <div className="flex justify-between space-x-6">
                    {customer.phone ? (
                      <div className="md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Phone
                        </p>
                        <p className="text-xs text-gray-600">
                          {customer.phone}
                        </p>
                      </div>
                    ) : (
                      <div className="text-start md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Phone
                        </p>
                        <p className="text-gray-500 text-xs">
                          No phone provided
                        </p>
                      </div>
                    )}

                    {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Language</p>
                      <p className="text-xs text-gray-600 capitalize">{customer.preferred_language}</p>
                    </div> */}

                    {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Tier</p>
                      <Badge className={customer.customer_tier === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                        {customer.customer_tier}
                      </Badge>
                    </div> */}

                    {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Status</p>
                      <Badge className={customer.account_status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 hover:bg-red-100 text-red-800'}>
                        {customer.account_status}
                      </Badge>
                    </div> */}

                    <div className="text-right md:text-center">
                      <p className="text-sm font-medium text-gray-900">
                        Date Joined
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(customer.created_at!).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => {
                      navigate(`/admin/customers/${customer.id}`);
                    }}
                    className="mt-4 md:mt-0"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  {/* <Button variant="ghost" size="sm">
                        <Edit
                          className="h-4 w-4"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setIsCustomerModalOpen(true);
                          }}
                        />
                      </Button> */}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 space-x-2">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalCustomers)} of{" "}
              {totalCustomers} customers
            </p>

            <div className="flex relative items-center justify-center pt-6 md:pt-0 space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <span className="text-sm absolute md:relative top-0 text-gray-600">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
